import React, { useEffect, useMemo, useRef, useState } from 'react';
import { addLead } from '../../services/leadService';
import { getAllCustomFields } from '../../services/customFieldServices';
import { getAllUsers } from '../../services/userServices';
import { formatStatusLabel } from '../../utils/statusFormatter';
import { useSettings } from '../../context/SettingsContext';
import { CrmButton, CrmDateInput, CrmDrawer, CrmInput, CrmSelect, CrmTextarea, CrmTimeInput } from '../ui';

const INITIAL_FORM = {
  first_name: '', last_name: '', email: '', phone_number: '', company_name: '', gst_number: '', contact_name: '',
  lead_status: 'new', priority: 'low', follow_up_date: '', assigned_salesperson: '', hotness: '', amount: '', notes: '',
};

const datePart = (value) => String(value || '').slice(0, 10);
const timePart = (value) => {
  const raw = String(value || '');
  const match = raw.match(/[T ](\d{2}:\d{2})/);
  return match?.[1] || '';
};
const combineDateTime = (date, time) => date ? `${date}T${time || '00:00'}` : '';

function AddLeadDialog({ open, onClose, onLeadCreated, showNotification, prefillName = '' }) {
  const { settings } = useSettings();
  const dateFormat = settings?.date_format || 'DD/MM/YYYY';
  const [form, setForm] = useState(INITIAL_FORM);
  const [users, setUsers] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [customValues, setCustomValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!open) { loadedRef.current = false; return; }
    if (prefillName) {
      const parts = prefillName.trim().split(/\s+/);
      setForm((current) => ({ ...current, first_name: parts[0] || '', last_name: parts.slice(1).join(' ') }));
    }
    if (loadedRef.current) return;
    loadedRef.current = true;
    Promise.all([getAllUsers(), getAllCustomFields()]).then(([userList, fields]) => {
      setUsers((Array.isArray(userList) ? userList : []).filter((user) => String(user.role_name || user.role || '').toLowerCase().includes('salesman')));
      setCustomFields(Array.isArray(fields) ? fields : []);
    }).catch(() => showNotification?.('Failed to load users/custom fields', 'error'));
  }, [open, prefillName, showNotification]);

  const customFieldRows = useMemo(() => customFields.filter((field) => field?.field_id), [customFields]);
  const setValue = (name, value) => setForm((current) => ({ ...current, [name]: value }));

  const close = () => {
    setForm(INITIAL_FORM);
    setCustomValues({});
    onClose?.();
  };

  const save = async () => {
    if (submitting) return;
    if (!form.first_name.trim()) return showNotification?.('First name is required', 'warning');
    setSubmitting(true);
    try {
      const numberOrNull = (value) => value === '' || value == null ? null : Number(value);
      const payload = {
        ...form,
        email: form.email || null,
        phone_number: form.phone_number || null,
        company_name: form.company_name || null,
        gst_number: form.gst_number || null,
        follow_up_date: form.follow_up_date || null,
        assigned_salesperson: form.assigned_salesperson || null,
        hotness: numberOrNull(form.hotness),
        amount: numberOrNull(form.amount),
        notes: form.notes || null,
        custom_fields: Object.entries(customValues).map(([id, value]) => ({ field_id: Number(id), field_value: value || null })),
      };
      const response = await addLead(payload);
      const id = response?.leadId || response?.id;
      if (!id) throw new Error('Contact created but no ID returned from server');
      showNotification?.('Contact created successfully', 'success');
      await onLeadCreated?.(id);
      close();
    } catch (error) {
      showNotification?.(error?.response?.data?.details || error?.response?.data?.error || error?.message || 'Failed to create contact', 'error');
    } finally { setSubmitting(false); }
  };

  return (
    <CrmDrawer
      open={open}
      onClose={close}
      title="Add contact"
      subtitle="Create the CRM contact without leaving the quotation."
      size="lg"
      footer={<><CrmButton variant="tertiary" onClick={close}>Cancel</CrmButton><div style={{ flex: 1 }} /><CrmButton variant="primary" loading={submitting} onClick={save}>Save contact</CrmButton></>}
    >
      <div className="crm-form-grid crm-form-grid--2">
        <CrmInput label="First name" required value={form.first_name} onChange={(e) => setValue('first_name', e.target.value)} />
        <CrmInput label="Last name" value={form.last_name} onChange={(e) => setValue('last_name', e.target.value)} />
        <CrmInput label="Email" type="email" value={form.email} onChange={(e) => setValue('email', e.target.value)} />
        <CrmInput label="Phone" type="tel" value={form.phone_number} onChange={(e) => setValue('phone_number', e.target.value)} />
        <CrmInput label="Company" value={form.company_name} onChange={(e) => setValue('company_name', e.target.value)} />
        <CrmInput label="GST number" value={form.gst_number} onChange={(e) => setValue('gst_number', e.target.value)} />
        <CrmSelect label="Status" value={form.lead_status} onChange={(e) => setValue('lead_status', e.target.value)}>{['new','in-progress','closed','won','lost'].map((v) => <option key={v} value={v}>{formatStatusLabel(v)}</option>)}</CrmSelect>
        <CrmSelect label="Priority" value={form.priority} onChange={(e) => setValue('priority', e.target.value)}>{['low','medium','high'].map((v) => <option key={v} value={v}>{formatStatusLabel(v)}</option>)}</CrmSelect>
        <CrmSelect label="Assigned salesperson" value={form.assigned_salesperson} onChange={(e) => setValue('assigned_salesperson', e.target.value)}><option value="">Unassigned</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}</CrmSelect>
        <CrmInput label="Hotness" type="number" min="0" step="1" value={form.hotness} onChange={(e) => setValue('hotness', e.target.value)} />
        <CrmDateInput label="Follow-up date" value={datePart(form.follow_up_date)} dateFormat={dateFormat} onChange={(date) => setValue('follow_up_date', combineDateTime(date, timePart(form.follow_up_date)))} />
        <CrmTimeInput label="Follow-up time" value={timePart(form.follow_up_date)} onChange={(e) => setValue('follow_up_date', combineDateTime(datePart(form.follow_up_date), e.target.value))} />
        <CrmInput label="Amount" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setValue('amount', e.target.value)} />
      </div>
      <CrmTextarea label="Notes" rows={4} value={form.notes} onChange={(e) => setValue('notes', e.target.value)} />
      {customFieldRows.length ? <div className="crm-form-grid crm-form-grid--2">{customFieldRows.map((field) => <CrmInput key={field.field_id} label={field.field_name} value={customValues[field.field_id] || ''} onChange={(e) => setCustomValues((current) => ({ ...current, [field.field_id]: e.target.value }))} />)}</div> : null}
    </CrmDrawer>
  );
}

export default AddLeadDialog;
